import { ensureBooleanSampleValues, isBooleanLike, isNumberLike } from '@gonfalon/dataviz-experimental/QueryBuilder';
import type { FieldOptions } from '@gonfalon/dataviz-experimental/types';
import { msg } from '@gonfalon/messages/msg';
import type { getSuggestions, GetSuggestionsRequestBody } from '@gonfalon/openapi/operations/getSuggestions';

import { CONTEXT_ATTRS_PREFIX, DATA_JSON_PREFIX } from './metricEventFilterFields';

export function displayFieldName(field: string): string {
  if (field.startsWith(CONTEXT_ATTRS_PREFIX)) {
    return field.slice(CONTEXT_ATTRS_PREFIX.length);
  }
  if (field.startsWith(DATA_JSON_PREFIX)) {
    return field.slice(DATA_JSON_PREFIX.length);
  }
  return field;
}

export function fieldCategoryFor(field: string): string {
  return field.startsWith(CONTEXT_ATTRS_PREFIX)
    ? msg('metrics.eventFilters.contextAttributesCategory')
    : msg('metrics.eventFilters.eventPropertiesCategory');
}

type SuggestionItem = NonNullable<NonNullable<Awaited<ReturnType<typeof getSuggestions>>['data']>['items']>[number];

// getSuggestions has no top-level `filters` (any shape → 400), so suggestions stay project-wide.
export function buildSuggestionsPayload(
  projectKey: string,
  from: number,
  to: number,
  contextKind?: string,
): GetSuggestionsRequestBody {
  return {
    projectKey,
    dataSource: {
      domain: 'sdk',
      kind: 'custom_event',
      timeRange: { from, to, timezone: 'America/New_York' },
    },
    suggestFields: {
      limit: 100, // max
      ...(contextKind ? { contextKind } : {}),
      withValues: { limit: 100 },
    },
  };
}

export function mapSuggestionsToFieldOptions(items: SuggestionItem[]): FieldOptions[] {
  const seenFields = new Set<string>();

  return items
    .map((item): FieldOptions => {
      const sampleValues = item.sampleValues ?? [];
      // Dedupe — react-aria keys options by value and crashes on duplicates.
      const seenSampleValues = new Set<string>();
      const mappedSampleValues = sampleValues.flatMap((sv) => {
        const value = String(sv.value);
        if (seenSampleValues.has(value)) {
          return [];
        }
        seenSampleValues.add(value);
        return [{ value, approxCount: sv.count }];
      });

      const field = item.field;
      const fieldCategory = fieldCategoryFor(field);

      const hasSamples = sampleValues.length > 0;
      const allNumber = hasSamples && sampleValues.every((sv) => isNumberLike(sv.value));
      const allBoolean = hasSamples && sampleValues.every((sv) => isBooleanLike(sv.value));
      const allString = hasSamples && sampleValues.every((sv) => typeof sv.value === 'string');

      if (allNumber) {
        return {
          field,
          fieldCategory,
          valueType: 'number',
          allowedOperators: ['=', '!=', '>', '>=', '<', '<='],
          sampleValues: mappedSampleValues,
          allowCustom: true,
        } satisfies FieldOptions;
      }
      if (allBoolean) {
        return {
          field,
          fieldCategory,
          valueType: 'boolean',
          allowedOperators: ['=', '!='],
          sampleValues: ensureBooleanSampleValues(mappedSampleValues, (booleanValue) => ({
            value: booleanValue,
            approxCount: 0,
          })),
          allowCustom: true,
        } satisfies FieldOptions;
      }
      if (allString) {
        return {
          field,
          fieldCategory,
          valueType: 'string',
          multi: true,
          allowedOperators: ['=', '!=', '//', '!//', '**', '^=', '=^'],
          sampleValues: mappedSampleValues,
          allowCustom: true,
        } satisfies FieldOptions;
      }
      // Untyped: equality only — other ops would be silently dropped by mapBarOp on save.
      return {
        field,
        fieldCategory,
        allowedOperators: ['=', '!='],
        sampleValues: mappedSampleValues,
        allowCustom: true,
      } satisfies FieldOptions;
    })
    .filter((option) => {
      // Drop kind-only entries (`context_attributes_json.<kind>`) — not a saveable attribute filter.
      if (
        option.field.startsWith(CONTEXT_ATTRS_PREFIX) &&
        !option.field.slice(CONTEXT_ATTRS_PREFIX.length).includes('.')
      ) {
        return false;
      }
      if (seenFields.has(option.field)) {
        return false;
      }
      seenFields.add(option.field);
      return true;
    })
    .map((option) => ({ ...option, displayValue: displayFieldName(option.field) }));
}
